<template>
    <div class="desktop-emulator-container">
        <!-- Background decoration / branding could go here -->
        <div class="emulator-background"></div>

        <div class="emulator-layout">
            <div class="emulator-wrapper">
                <div class="emulator-sidebar">
                    <Transition name="slide-fade" mode="out-in">
                        <div v-if="showQrCode" class="qr-hint-card">
                            <button class="close-qr-button" @click="showQrCode = false" aria-label="Close">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                            </button>
                            <div class="logo-container">
                                <img
                                    src="@/assets/images/brand/Santina-golden.svg"
                                    alt="Santina"
                                    class="brand-logo"
                                />
                            </div>
                            <h3>{{ $t('general.mobile_recommended_title', 'Best viewed on mobile') }}</h3>
                            <p>{{ $t('general.scan_with_camera') }}</p>
                            <div class="qr-container">
                                <div class="qr-border">
                                    <div class="qr-wrapper">
                                        <img
                                            v-if="qrCodeDataUrl"
                                            :src="qrCodeDataUrl"
                                            :alt="$t('general.scan_qr_code')"
                                            class="qr-code"
                                        />
                                        <img
                                            src="@/assets/images/brand/Santina-icon.svg"
                                            class="qr-logo"
                                            alt="Santina"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        
                        <button v-else class="qr-trigger-button" @click="showQrCode = true">
                            <div class="qr-trigger-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <rect x="7" y="7" width="3" height="3"></rect>
                                    <rect x="14" y="7" width="3" height="3"></rect>
                                    <rect x="7" y="14" width="3" height="3"></rect>
                                    <rect x="14" y="14" width="3" height="3"></rect>
                                </svg>
                            </div>
                            <span>{{ $t('general.scan_qr_code', 'Scan to open on mobile') }}</span>
                        </button>
                    </Transition>
                </div>
                <div class="emulator-device-frame">
                    <div class="emulator-screen">
                        <iframe
                        ref="appIframe"
                        :src="iframeSrc"
                        frameborder="0"
                        class="app-iframe"
                        allow="camera; microphone; display-capture; autoplay; clipboard-write; clipboard-read"
                        @load="onIframeLoad"
                        ></iframe>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, watch, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import QRCode from 'qrcode'

const { t, locale } = useI18n()
const showQrCode = ref(false)
const route = useRoute()
const router = useRouter()
const appIframe = ref(null)

// Initialize iframe with current URL, appending a query parameter to tell the iframe it's being emulated
const initialSrc = new URL(window.location.href)
initialSrc.searchParams.set('emulated', '1')
const iframeSrc = ref(initialSrc.toString())

const isIframeLoaded = ref(false)

const onIframeLoad = () => {
    isIframeLoaded.value = true
}

const currentUrl = computed(() => window.location.href)
const qrCodeDataUrl = ref('')

const generateQRCode = async () => {
    try {
        qrCodeDataUrl.value = await QRCode.toDataURL(currentUrl.value, {
            errorCorrectionLevel: 'H',
            margin: 1,
            width: 200,
            color: {
                dark: '#000000',
                light: '#ffffff',
            },
        })
    } catch (err) {
        console.error('Error generating QR code:', err)
    }
}

watch(currentUrl, generateQRCode)

// Listen for messages from the iframe
const handleMessage = (event) => {
    // Basic security check: ensure message comes from same origin
    if (event.origin !== window.location.origin) return

    const data = event.data
    
    // Check if it's a route change message from our app inside the iframe
    if (data && data.type === 'SANTINA_ROUTE_CHANGE') {
        const newPath = data.path
        
        // Update the parent URL without triggering a full page reload or router navigation
        // (because the actual app is running inside the iframe)
        if (newPath !== route.fullPath) {
            window.history.replaceState(null, '', newPath)
        }
    } else if (data && data.type === 'SANTINA_LANGUAGE_CHANGE') {
        // Sync language from iframe to parent window
        locale.value = data.locale
        localStorage.setItem('preferredLanguage', data.locale)
    }
}

onMounted(() => {
    window.addEventListener('message', handleMessage)
    generateQRCode()
})

onUnmounted(() => {
    window.removeEventListener('message', handleMessage)
})

// Listen to popstate (back/forward buttons on the parent browser)
// and forward them to the iframe
const handlePopState = () => {
    if (appIframe.value && appIframe.value.contentWindow) {
        // Send a message to the iframe to navigate
        appIframe.value.contentWindow.postMessage({
            type: 'SANTINA_NAVIGATE',
            path: window.location.pathname + window.location.search + window.location.hash
        }, window.location.origin)
    }
}

onMounted(() => {
    window.addEventListener('popstate', handlePopState)
    
    // Check initial stored language on mount as well
    const storedLang = localStorage.getItem('preferredLanguage')
    if (storedLang && storedLang !== locale.value) {
        locale.value = storedLang
    }
})

onUnmounted(() => {
    window.removeEventListener('popstate', handlePopState)
})
</script>

<style scoped>
.desktop-emulator-container {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: #0a0a0a;
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 99999;
}

.emulator-background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    /* Subtle gradient or pattern could go here */
    background: radial-gradient(circle at center, #1a1a1a 0%, #000000 100%);
    opacity: 0.8;
}

.emulator-layout {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    position: relative;
    z-index: 10;
}

.emulator-wrapper {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    gap: 24px;
}

.emulator-device-frame {
    position: relative;
    width: 400px;
    height: 720px;
    flex-shrink: 0;
    background-color: #000;
    border-radius: 40px; /* Thicker rounded corners for the outer frame */
    box-shadow: 
        0 0 0 10px #111,
        0 20px 50px rgba(0, 0, 0, 0.5),
        inset 0 0 0 2px rgba(255, 255, 255, 0.1);
    display: flex;
    justify-content: center;
    align-items: center;
    overflow: hidden;
    padding: 12px; /* Pad inner elements to not cut into corners */
}

.emulator-screen {
    width: 100%;
    height: 100%;
    background-color: #fff; /* or match app background */
    border-radius: 28px; /* Slightly less round than the outer frame to prevent white bleed */
    overflow: hidden;
    position: relative;
    border: 1px solid #1a1a1a;
}

.app-iframe {
    width: 100%;
    height: 100%;
    border: none;
    display: block;
    background-color: transparent;
    scrollbar-width: none;
    -ms-overflow-style: none;
}

/* Custom scrollbar for Webkit browsers */
.app-iframe::-webkit-scrollbar {
    display: none;
}

.emulator-sidebar {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
}

.qr-trigger-button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    background: #111111;
    border: 1px solid #222;
    padding: 1.25rem 0.75rem;
    border-radius: 16px;
    color: #9ca3af;
    cursor: pointer;
    transition: all 0.2s ease;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    width: 110px;
}

.qr-trigger-button:hover {
    background: #1a1a1a;
    border-color: rgba(155, 110, 43, 0.5);
    color: #fff;
}

.qr-trigger-icon {
    width: 44px;
    height: 44px;
    border-radius: 12px;
    background: rgba(155, 110, 43, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    color: #9b6e2b;
}

.qr-trigger-button span {
    font-size: 13px;
    text-align: center;
    line-height: 1.3;
}

.qr-hint-card {
    position: relative;
    background: #111111;
    padding: 2rem;
    border-radius: 16px;
    border: 1px solid #222;
    text-align: center;
    width: 300px; /* Fixed width */
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
    color: white;
}

.close-qr-button {
    position: absolute;
    top: 0.75rem;
    right: 0.75rem;
    background: rgba(255, 255, 255, 0.05);
    border: none;
    color: #9ca3af;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
}

.close-qr-button:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
}

/* Animations */
.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateX(-10px);
}

.logo-container {
    margin-bottom: 1.5rem;
    display: flex;
    justify-content: center;
}

.brand-logo {
    height: 40px;
    width: auto;
}

.qr-hint-card h3 {
    font-size: 20px;
    margin-bottom: 10px;
    font-weight: 600;
    background: linear-gradient(to right, #9b6e2b, #996923);
    -webkit-background-clip: text;
    background-clip: text;
    color: transparent;
}

.qr-hint-card p {
    font-size: 14px;
    color: #9ca3af;
    margin-bottom: 20px;
    line-height: 1.5;
}

.qr-container {
    display: flex;
    justify-content: center;
}

.qr-border {
    padding: 3px;
    background: linear-gradient(135deg, #9b6e2b 0%, #996923 100%);
    border-radius: 12px;
    display: inline-block;
}

.qr-wrapper {
    position: relative;
    width: 160px;
    height: 160px;
    border-radius: 10px;
    overflow: hidden;
    background-color: white;
    display: flex;
    justify-content: center;
    align-items: center;
}

.qr-code {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.qr-logo {
    position: absolute;
    width: 32px;
    height: 32px;
    background: white;
    border-radius: 8px;
    padding: 2px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 10;
}

@media (max-width: 1100px) {
    /* When screen gets somewhat small, hide the sidebar so emulator stays centered */
    .emulator-sidebar {
        display: none;
    }
}

@media (max-width: 450px) {
    /* If the screen gets too small for the frame, just remove the frame styling */
    .emulator-device-frame {
        width: 100vw;
        height: 100vh;
        max-height: 100vh;
        border-radius: 0;
        box-shadow: none;
        padding: 0;
    }
    
    .emulator-screen {
        border-radius: 0;
        border: none;
    }
}
</style>
